import type { AxiosError } from "axios";

/**
 * Handled client outcomes: the UI already reacts to these, so a Sentry event
 * for them is noise. Everything else (5xx, unexpected codes) is a defect.
 */
const HANDLED_STATUSES = new Set([400, 401, 402, 403, 404, 409, 422]);

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

/**
 * Collapses a request URL to its route shape: scheme, host, query and hash
 * are dropped, UUID and numeric path segments become `:id`.
 */
export function routeTemplate(url: string | undefined): string {
  if (!url) return "unknown";
  const path = url
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "")
    .split(/[?#]/)[0];
  return path
    .split("/")
    .map((segment) =>
      UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)
        ? ":id"
        : segment,
    )
    .join("/");
}

export type ApiErrorCaptureContext = {
  fingerprint: string[];
  tags: { api_route: string; api_status: string };
};

/**
 * Decides whether a failed request reaches Sentry and how it groups.
 * Returns null for handled 4xx and for requests that never got a response
 * (network error, abort, timeout); otherwise a fingerprint of
 * method + route template + status so each failing route is its own issue.
 */
export function apiErrorCaptureContext(
  error: AxiosError,
): ApiErrorCaptureContext | null {
  const status = error.response?.status;
  if (status === undefined || HANDLED_STATUSES.has(status)) return null;

  const method = (error.config?.method ?? "unknown").toUpperCase();
  const route = routeTemplate(error.config?.url);
  const apiStatus = String(status);
  return {
    fingerprint: [method, route, apiStatus],
    tags: { api_route: route, api_status: apiStatus },
  };
}
